/** My Students page — shows guardian's linked students. */

import React from 'react';
import { usePortalStudents, type PortalStudent } from '../api/portal';

const card: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: '0.5rem',
  padding: '1rem 1.25rem',
  marginBottom: '0.75rem',
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const colour = status === 'active' ? '#10b981' : status === 'inactive' ? '#f59e0b' : '#6b7280';
  return (
    <span style={{
      background: colour + '22',
      color: colour,
      padding: '2px 8px',
      borderRadius: 12,
      fontSize: '0.75rem',
      fontWeight: 600,
      textTransform: 'capitalize',
    }}>
      {status}
    </span>
  );
};

const StudentCard: React.FC<{ student: PortalStudent }> = ({ student }) => (
  <div style={card} data-testid={`candeza-portal-student-row-${student.id}`}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div>
        <strong data-testid="candeza-portal-student-name">
          {student.first_name} {student.last_name}
        </strong>
        {student.date_of_birth && (
          <div style={{ fontSize: '0.8125rem', color: '#6b7280', marginTop: 2 }}>
            DOB: {student.date_of_birth}
          </div>
        )}
      </div>
      <StatusBadge status={student.status} />
    </div>
    {student.notes && (
      <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
        {student.notes}
      </p>
    )}
  </div>
);

const MyStudentsPage: React.FC = () => {
  const { data, isLoading, isError } = usePortalStudents();

  if (isLoading) {
    return (
      <div data-testid="candeza-portal-students-loading">
        <p style={{ color: '#6b7280' }}>Loading students…</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div data-testid="candeza-portal-students-error" style={{ color: '#ef4444' }}>
        Could not load students. Please try again.
      </div>
    );
  }

  const students = data?.items ?? [];

  return (
    <div data-testid="candeza-portal-students-page">
      <h1 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '1.25rem' }}>
        My Students
      </h1>
      {students.length === 0 ? (
        <p data-testid="candeza-portal-students-empty" style={{ color: '#6b7280' }}>
          No students linked to your account yet. Contact the school to link your student.
        </p>
      ) : (
        <div data-testid="candeza-portal-students-list">
          {students.map(s => <StudentCard key={s.id} student={s} />)}
        </div>
      )}
    </div>
  );
};

export default MyStudentsPage;

